import { Injectable } from '@angular/core';

import {
  USER_ADMIN_BRIDGE_VALUES,
  USER_ADMIN_CONTACT_MARKERS,
  USER_ADMIN_FORM_VALUES,
  USER_ADMIN_GMA,
  USER_ADMIN_REGION_MARKERS,
  UserAdminGmaWebcamValue,
} from '../constants/user-admin-contract.constants';

export interface BillingAddressParts {
  company: string;
  street: string;
  zip: string;
  city: string;
}

export interface ParsedContactRow {
  name: string;
  email: string;
  phone: string;
}

export interface ParsedContactInfo {
  contactRows: ParsedContactRow[];
  billingAddress: BillingAddressParts;
  billingEmail: string;
}

export interface ParsedGmaRow {
  coordinates: string;
  manufacturer: string;
  webcam: UserAdminGmaWebcamValue;
  productKey: string;
}

export interface ParsedRegionInfo {
  locations: string;
  localFeatures: string;
  elevationMin: number | null;
  elevationMax: number | null;
  bridges: string;
  hasGma: string;
  hasGmaDetails: boolean;
  gmaDetailsPart: string;
}

export interface NormalizedGmaManufacturer {
  manufacturer: string;
  productKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserAdminContractParserService {
  extractSection(
    text: string | null | undefined,
    startMarker: string,
    endMarker: string
  ): string {
    if (!text || !text.includes(startMarker)) {
      return '';
    }

    const startIndex = text.indexOf(startMarker) + startMarker.length;
    const endIndex = endMarker
      ? text.indexOf(endMarker, startIndex)
      : text.length;

    return text.substring(startIndex, endIndex).trim();
  }

  sanitizeValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value.trim() === USER_ADMIN_FORM_VALUES.emptyPlaceholder
      ? ''
      : value;
  }

  parseContactInfo(text: string | null | undefined): ParsedContactInfo {
    const contactsPart =
      this.extractSection(
        text,
        USER_ADMIN_CONTACT_MARKERS.contacts,
        USER_ADMIN_CONTACT_MARKERS.billing
      ) || text;

    const billingAddressText = this.extractSection(
      text,
      USER_ADMIN_CONTACT_MARKERS.billingAddress,
      USER_ADMIN_CONTACT_MARKERS.billingEmail
    );

    const billingEmail = this.extractSection(
      text,
      USER_ADMIN_CONTACT_MARKERS.billingEmail,
      ''
    );

    return {
      contactRows: this.parseContactRows(contactsPart),
      billingAddress: this.parseBillingAddress(billingAddressText),
      billingEmail: billingEmail?.trim() || '',
    };
  }

  parseRegionInfo(text: string | null | undefined): ParsedRegionInfo {
    const locations = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.locations,
      USER_ADMIN_REGION_MARKERS.localFeatures
    );

    const localFeatures = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.localFeatures,
      'Höhenlage:'
    );

    const elevationMinRaw = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.elevationMin,
      USER_ADMIN_REGION_MARKERS.elevationUnit
    );

    const elevationMaxRaw = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.elevationMax,
      USER_ADMIN_REGION_MARKERS.elevationUnit
    );

    const bridgesRaw = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.bridges,
      USER_ADMIN_REGION_MARKERS.gmaPlants
    );

    const hasGmaText = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.gmaExists,
      USER_ADMIN_REGION_MARKERS.gmaDetails
    );

    const gmaDetailsPart = this.extractSection(
      text,
      USER_ADMIN_REGION_MARKERS.gmaDetails,
      ''
    );

    const bridges = USER_ADMIN_BRIDGE_VALUES.includes(bridgesRaw as any)
      ? bridgesRaw
      : USER_ADMIN_FORM_VALUES.few;

    return {
      locations: this.sanitizeValue(locations),
      localFeatures: this.sanitizeValue(localFeatures),
      elevationMin: this.parseNullableNumber(elevationMinRaw),
      elevationMax: this.parseNullableNumber(elevationMaxRaw),
      bridges,
      hasGma:
        hasGmaText === USER_ADMIN_FORM_VALUES.yesUppercase
          ? USER_ADMIN_FORM_VALUES.yes
          : USER_ADMIN_FORM_VALUES.no,
      hasGmaDetails:
        hasGmaText === USER_ADMIN_FORM_VALUES.yesUppercase && !!gmaDetailsPart,
      gmaDetailsPart,
    };
  }

  parseNullableNumber(value: string): number | null {
    if (
      !value ||
      value.trim() === USER_ADMIN_FORM_VALUES.emptyPlaceholder ||
      Number.isNaN(Number(value))
    ) {
      return null;
    }

    return Number(value);
  }

  parseBillingAddress(billingAddress: string): BillingAddressParts {
    const fallback: BillingAddressParts = {
      company: '',
      street: '',
      zip: '',
      city: '',
    };

    if (
      !billingAddress ||
      billingAddress.trim() === USER_ADMIN_FORM_VALUES.emptyPlaceholder
    ) {
      return fallback;
    }

    const addressLines = billingAddress
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    if (addressLines.length >= 3) {
      const zipCity = this.parseZipCityLine(addressLines[2]);

      return {
        company: addressLines[0],
        street: addressLines[1],
        zip: zipCity.zip,
        city: zipCity.city,
      };
    }

    if (addressLines.length === 2) {
      const firstLineStartsWithNumber = !Number.isNaN(
        Number.parseInt(addressLines[0].charAt(0), 10)
      );
      const zipCity = this.parseZipCityLine(addressLines[1]);

      return {
        company: firstLineStartsWithNumber ? '' : addressLines[0],
        street: firstLineStartsWithNumber ? addressLines[0] : '',
        zip: zipCity.zip,
        city: zipCity.city,
      };
    }

    if (addressLines.length === 1) {
      const zipCity = this.parseZipCityLine(addressLines[0]);

      return {
        ...fallback,
        zip: zipCity.zip,
        city: zipCity.city,
      };
    }

    return fallback;
  }

  parseContactRows(
    contactsPart: string | null | undefined
  ): ParsedContactRow[] {
    if (
      !contactsPart ||
      contactsPart.trim() === '' ||
      contactsPart.trim() === USER_ADMIN_FORM_VALUES.emptyPlaceholder
    ) {
      return [];
    }

    return contactsPart
      .split('\n')
      .map(line => line.trim())
      .filter(line => this.isContactLine(line))
      .map(line => this.parseContactLine(line))
      .filter(contactRow => this.hasContactContent(contactRow));
  }

  parseGmaRows(gmaDetailsPart: string | null | undefined): ParsedGmaRow[] {
    if (!gmaDetailsPart) {
      return [];
    }

    return gmaDetailsPart
      .split('--- Anlage')
      .map(block => block.trim())
      .filter(Boolean)
      .map(block => this.parseGmaBlock(block));
  }

  normalizeGmaManufacturer(rawManufacturer: string): NormalizedGmaManufacturer {
    let manufacturer = rawManufacturer;
    let productKey = '';

    if (rawManufacturer.startsWith('Campbell')) {
      if (
        rawManufacturer.includes(USER_ADMIN_REGION_MARKERS.productKeySeparator)
      ) {
        const parts = rawManufacturer.split(
          USER_ADMIN_REGION_MARKERS.productKeySeparator
        );

        productKey = parts[1]?.trim() || '';

        const namePart = parts[0].replace('Campbell', '').trim();
        manufacturer = `Campbell; Modell: ${namePart || 'Wintersense Solar'}`;
      } else {
        const namePart = rawManufacturer.replace('Campbell', '').trim();
        manufacturer = `Campbell; Modell: ${namePart || 'Wintersense Solar'}`;
      }

      return {
        manufacturer,
        productKey,
      };
    }

    if (
      (rawManufacturer.startsWith('Lufft') ||
        rawManufacturer.startsWith('Teconer')) &&
      !rawManufacturer.includes('; Modell: ')
    ) {
      const spaceIndex = rawManufacturer.indexOf(' ');

      if (spaceIndex > 0) {
        const brand = rawManufacturer.substring(0, spaceIndex);
        const model = rawManufacturer.substring(spaceIndex + 1);

        manufacturer = `${brand}; Modell: ${model}`;
      }
    }

    return {
      manufacturer,
      productKey,
    };
  }

  private parseGmaBlock(block: string): ParsedGmaRow {
    const coordinates = this.extractSection(
      block,
      USER_ADMIN_REGION_MARKERS.gmaCoordinates,
      USER_ADMIN_REGION_MARKERS.gmaManufacturer
    );

    const rawManufacturer = this.extractSection(
      block,
      USER_ADMIN_REGION_MARKERS.gmaManufacturer,
      USER_ADMIN_REGION_MARKERS.gmaWebcam
    );

    const webcamText = this.extractSection(
      block,
      USER_ADMIN_REGION_MARKERS.gmaWebcam,
      ''
    );

    const normalizedManufacturer =
      this.normalizeGmaManufacturer(rawManufacturer);

    return {
      coordinates: this.sanitizeValue(coordinates),
      manufacturer: this.sanitizeValue(normalizedManufacturer.manufacturer),
      webcam: webcamText.includes(USER_ADMIN_GMA.webcamAvailableText)
        ? USER_ADMIN_GMA.availableWebcamValue
        : USER_ADMIN_GMA.defaultWebcamValue,
      productKey: this.sanitizeValue(normalizedManufacturer.productKey),
    };
  }

  private parseZipCityLine(value: string): { zip: string; city: string } {
    const zipMatch = value.match(/^(\d{5}|\d{4})\s+(.+)$/);

    if (!zipMatch) {
      return {
        zip: '',
        city: value,
      };
    }

    return {
      zip: zipMatch[1],
      city: zipMatch[2],
    };
  }

  private isContactLine(line: string): boolean {
    const pipeCount = (line.match(/\|/g) || []).length;
    const isSystemLine = line.startsWith('===');

    return line !== '' && pipeCount === 2 && !isSystemLine;
  }

  private parseContactLine(line: string): ParsedContactRow {
    const [name, email, phone] = line.split('|').map(value => value.trim());

    return {
      name: this.sanitizeValue(name),
      email: this.sanitizeValue(email),
      phone: this.sanitizeValue(phone),
    };
  }

  private hasContactContent(contactRow: ParsedContactRow): boolean {
    return Boolean(contactRow.name || contactRow.email || contactRow.phone);
  }
}
